import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../../../contexts/SubscriptionContext";
import { getAccessToken } from "../../../awsAuth";
import { PLANS } from "./plans";
import Checkout from "./Checkout";
import "./Subscription.scss";
import DynamicHeader from "../../Header/DynamicHeader";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

function normalizePlan(plan) {
  const p = String(plan || "free").toLowerCase();
  if (["plus", "business"].includes(p)) return p;
  return "free";
}

function formatDateShort(ts) {
  if (!ts) return null;

  const d = new Date(Number(ts));
  if (Number.isNaN(d.getTime())) return null;

  return new Intl.DateTimeFormat("uk-UA", {
    timeZone: "Europe/Kyiv",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(d);
}

export default function Subscription() {
  const navigate = useNavigate();
  const subscription = useSubscription();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialError, setTrialError] = useState("");

  const trial = subscription?.trial || {};
  const currentPlan = normalizePlan(subscription?.plan);
  const planUntil = subscription?.planUntil || subscription?.premiumUntil || 0;

  const untilText = useMemo(() => {
    if (currentPlan === "free") return null;
    return formatDateShort(planUntil);
  }, [currentPlan, planUntil]);

  const selected = selectedPlan || currentPlan;

  // Backend is the sole source of truth for eligibility/one-time-use — this
  // only decides which button/confirmation text to show, never whether the
  // trial is actually allowed. A rejected activation (already used, already
  // paid, lost a race) just surfaces the server's own error message.
  const handleStartTrial = async (planKey) => {
    const planLabel = planKey === "business" ? "Cvanto Business" : "Cvanto Plus";
    const ok = window.confirm(
      `Активувати ${planLabel} на 7 днів безкоштовно? Картка не потрібна, автоматичного списання не буде.`
    );
    if (!ok) return;

    setTrialBusy(true);
    setTrialError("");

    try {
      const token = await getAccessToken?.().catch(() => null);

      if (!token) {
        setTrialError("Увійдіть у акаунт, щоб активувати пробний період.");
        return;
      }

      const res = await fetch(`${API_BASE}/api/subscription/trial`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan: planKey }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося активувати пробний період");
      }

      await subscription.refresh();
    } catch (e) {
      setTrialError(e.message || "Не вдалося активувати пробний період");
    } finally {
      setTrialBusy(false);
    }
  };

  return (
    <>
      <DynamicHeader
        title="Ваш тариф"
        variant="glass"
        size="regular"
        showBack
        withSpacer
      />

      <div className="subscription-page">
        <div className="subscription-top">
          <button
            type="button"
            className="subscription-back"
            onClick={() => navigate(-1)}
            aria-label="Назад"
          >
            ←
          </button>

          <div>
            <h1>Ваш тариф</h1>
            <p>
              Поточний тариф: <b>{currentPlan === "free" ? "Free" : currentPlan === "plus" ? "Plus" : "Business"}</b>
              {trial.active ? " (пробний період)" : ""}
              {untilText ? <> до <b>{untilText}</b></> : null}
            </p>
          </div>
        </div>

        <div className="subscription-grid">
          {PLANS.map((item) => {
            const isCurrent = item.key === currentPlan;
            const isSelected = item.key === selected;
            const isTrialActiveHere = trial.active && trial.plan === item.key;
            const canTrialThis = item.key !== "free" && !isCurrent && trial.eligible;

            return (
              <article
                key={item.key}
                className={[
                  "subscription-card",
                  isCurrent ? "is-current" : "",
                  isSelected ? "is-selected" : "",
                  item.key === "business" ? "is-business" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => setSelectedPlan(item.key)}
              >
                <div className="subscription-card-head">
                  <div>
                    <h2>{item.name}</h2>
                    <div className="subscription-price">
                      {isTrialActiveHere ? "Пробний період" : item.price}
                    </div>
                  </div>

                  {isCurrent && (
                    <span className="subscription-current-badge">
                      {isTrialActiveHere ? "Пробний" : "Поточний"}
                    </span>
                  )}
                </div>

                <p className="subscription-subtitle">
                  {isTrialActiveHere && untilText
                    ? `Активний до ${untilText}`
                    : item.subtitle}
                </p>
                <p className="subscription-description">{item.description}</p>

                <ul className="subscription-features">
                  {item.features.map((feature) => (
                    <li key={feature}>
                      <span>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className={[
                    "subscription-btn",
                    isCurrent ? "is-disabled" : "",
                    item.key !== "free" && !isCurrent ? "is-primary" : "",
                  ].filter(Boolean).join(" ")}
                  disabled={isCurrent || item.key === "free" || trialBusy}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.key === "free" || isCurrent) return;
                    if (canTrialThis) {
                      handleStartTrial(item.key);
                      return;
                    }
                    setCheckoutPlan(item.key);
                  }}
                >
                  {isCurrent
                    ? isTrialActiveHere
                      ? `Активний до ${untilText || "—"}`
                      : "Поточний тариф"
                    : item.key === "free"
                    ? "Базовий тариф"
                    : canTrialThis
                    ? `Спробувати ${item.key === "business" ? "Business" : "Plus"} 7 днів безкоштовно`
                    : "Оформити підписку"}
                </button>

                {canTrialThis && (
                  <p className="subscription-trial-hint">
                    7 днів безкоштовно. Без автоматичного списання.
                  </p>
                )}
              </article>
            );
          })}
        </div>

        {trialError && (
          <div className="subscription-trial-error">{trialError}</div>
        )}

        <div className="subscription-note-box">
          <h3>Важливо</h3>
          <p>
            Тариф не активується з браузера або localStorage. Усі права, ліміти,
            реклама, сторіс, бейджі та бізнес-модулі перевіряються тільки на сервері.
          </p>
        </div>
      </div>

      {checkoutPlan && (
        <Checkout
          planKey={checkoutPlan}
          onClose={() => setCheckoutPlan(null)}
        />
      )}
    </>
  );
}